import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, StatCard } from '@/components/ui/Card';
import { BarList, DimensionTrendChart, RadarChart, TrendChart } from '@/components/ui/Charts';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { Select } from '@/components/ui/Field';
import { Button } from '@/components/ui/Button';
import { PageHeader } from '@/app/PlaceholderPage';
import { cn } from '@/lib/cn';
import { useAuth } from '@/features/auth/AuthContext';
import { databaseApi } from '@/features/database/api';
import { analyticsApi, type TbmReport } from './api';
import { round1, shortPeriod } from './format';
import { TbmReportPdfModal } from './TbmReportPdfModal';

/** L-13 — Rapor satu TBM: rincian per dimensi dan tren antar semester. */
export function TbmReportPage() {
  const { user } = useAuth();
  const isPelita = user?.role === 'pelita';
  const [params, setParams] = useSearchParams();
  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  // Jika login sebagai PELITA, kunci ID ke tbmId naungan dirinya
  const tbmId = isPelita ? (user?.tbmId || params.get('tbm') || '') : (params.get('tbm') ?? '');

  const list = useQuery({
    queryKey: ['tbm', 'options'],
    queryFn: () => databaseApi.listTbm({ page: 1 }),
    enabled: !isPelita,
  });

  const report = useQuery({
    queryKey: ['analytics', 'tbm', tbmId],
    queryFn: () => analyticsApi.tbmReport(tbmId),
    enabled: Boolean(tbmId),
  });

  const options = list.data?.data ?? [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Rapor TBM"
        description={
          isPelita
            ? 'Rincian indeks keberdayaan TBM naungan Anda per dimensi dan perkembangannya.'
            : 'Rincian indeks keberdayaan satu TBM per dimensi, beserta pergerakannya antar semester.'
        }
        action={
          report.data?.latest && (
            <Button
              className="no-print"
              variant="secondary"
              onClick={() => setPdfModalOpen(true)}
            >
              📄 Cetak / Unduh PDF
            </Button>
          )
        }
      />

      {!isPelita && (
        <Card className="no-print">
          <Select
            label="Pilih TBM"
            value={tbmId}
            onChange={(e) => setParams(e.target.value ? { tbm: e.target.value } : {})}
          >
            <option value="">— pilih TBM —</option>
            {options.map((t) => (
              <option key={t.id} value={t.id}>
                {t.code} · {t.name}
              </option>
            ))}
          </Select>
        </Card>
      )}

      {report.data?.latest && (
        <TbmReportPdfModal
          open={pdfModalOpen}
          onClose={() => setPdfModalOpen(false)}
          data={report.data}
          isPelita={isPelita}
        />
      )}

      {!tbmId ? (
        <Card padded={false}>
          <EmptyState
            icon="🏫"
            title="Pilih TBM terlebih dahulu"
            description="Rapor menampilkan penilaian yang sudah dikirim atau diverifikasi."
          />
        </Card>
      ) : report.error ? (
        <ErrorState message={(report.error as Error).message} onRetry={() => report.refetch()} />
      ) : report.isLoading || !report.data ? (
        <Skeleton className="h-72 w-full" />
      ) : !report.data.latest ? (
        <Card padded={false}>
          <EmptyState
            icon="📄"
            title="Belum ada penilaian terkirim"
            description="TBM ini belum punya penilaian berstatus Terkirim atau Terverifikasi, sehingga belum ada yang bisa dilaporkan."
          />
        </Card>
      ) : (
        <TbmReportBody data={report.data} isPelita={isPelita} />
      )}
    </div>
  );
}

const TBM_BANDS_DEFINITIONS = [
  { name: 'Berdampak', range: '86 – 100', desc: 'TBM menjadi pusat belajar dan pemberdayaan masyarakat yang berkelanjutan serta memberi manfaat luas.' },
  { name: 'Mandiri', range: '71 – 85', desc: 'TBM mampu mengelola program secara mandiri, membangun kolaborasi, dan menghasilkan dampak yang terukur.' },
  { name: 'Berkembang', range: '51 – 70', desc: 'TBM mampu mengelola program secara terencana, rutin, dan mulai membangun jejaring serta partisipasi masyarakat.' },
  { name: 'Bertumbuh', range: '26 – 50', desc: 'TBM mulai memiliki sistem pengelolaan dan program literasi, namun belum konsisten.' },
  { name: 'Merintis', range: '< 26', desc: 'TBM baru mulai membangun tata kelola dan program dasar.' },
];

function TbmIdentityCard({ tbm }: { tbm: TbmReport['tbm'] }) {
  const regionText = tbm.regionPath?.map((r) => r.name).join(' → ') || 'Belum diatur';

  return (
    <Card className="border-l-4 border-l-primary bg-primary-soft/10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-primary text-white">
              {tbm.code}
            </span>
            {tbm.angkatan && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                Angkatan {tbm.angkatan}
              </span>
            )}
            <h2 className="text-xl font-bold text-ink">{tbm.name}</h2>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm text-ink-secondary">
            <div>
              <span className="text-ink-muted">Wilayah:</span>{' '}
              <strong className="text-ink font-medium">{regionText}</strong>
            </div>
            {tbm.address && (
              <div>
                <span className="text-ink-muted">Alamat:</span>{' '}
                <span className="text-ink">{tbm.address}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function BandDefinitionsCard({
  currentBand,
  bands,
}: {
  currentBand?: string | null;
  bands?: TbmReport['bands'];
}) {
  const list =
    bands && bands.length > 0
      ? bands.map((b) => ({
          name: b.name,
          range: b.display || (b.gt !== null ? `> ${b.gt}` : '≤ batas bawah'),
          desc:
            b.characteristic ||
            TBM_BANDS_DEFINITIONS.find(
              (d) => d.name.toLowerCase() === b.name.toLowerCase(),
            )?.desc ||
            '—',
        }))
      : TBM_BANDS_DEFINITIONS;

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[16px] font-semibold text-ink">
          Definisi Kategori Indeks Keberdayaan TBM
        </h3>
        <span className="text-xs text-ink-muted">Skala 0 – 100</span>
      </div>
      <div className="grid gap-2.5 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {list.map((def) => {
          const isCurrent = currentBand?.toLowerCase() === def.name.toLowerCase();
          return (
            <div
              key={def.name}
              className={cn(
                'rounded-lg p-3 text-xs border transition-all',
                isCurrent
                  ? 'border-primary bg-primary-soft/40 shadow-xs ring-1 ring-primary'
                  : 'border-border bg-subtle/50 text-ink-secondary',
              )}
            >
              <div className="flex items-center justify-between gap-1 mb-1">
                <span className={cn('font-bold text-[13px]', isCurrent ? 'text-primary' : 'text-ink')}>
                  {def.name}
                </span>
                <span className="font-semibold text-ink-muted tabular">{def.range}</span>
              </div>
              <p className="line-clamp-4 text-ink-secondary leading-relaxed">{def.desc}</p>
              {isCurrent && (
                <div className="mt-2 inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-semibold text-primary">
                  ★ Posisi Saat Ini
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function TbmReportBody({ data, isPelita }: { data: TbmReport; isPelita?: boolean }) {
  const { tbm, latest, dimensions, trend } = data;
  const [trendView, setTrendView] = useState<'dimensions' | 'overall'>('dimensions');

  if (!latest) return null;

  const trendPoints = trend
    .filter((t) => t.index !== null)
    .map((t) => ({ label: shortPeriod(t.periodName), value: t.index as number }));

  return (
    <div className="flex flex-col gap-5">
      <TbmIdentityCard tbm={tbm} />

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label="Indeks keberdayaan"
          value={latest.index === null ? '—' : round1(latest.index)}
          hint={latest.periodName}
        />
        <StatCard label="Kategori" value={latest.band ?? '—'} hint="hasil pembekuan saat kirim" />
        <StatCard label="Tahun program" value={latest.programYear ?? '—'} />
      </div>

      <BandDefinitionsCard currentBand={latest.band} bands={data.bands} />

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <Card>
          <h2 className="mb-3 text-[17px] font-semibold">Profil dimensi</h2>
          <RadarChart
            items={dimensions.map((d) => ({
              label: d.code,
              value: d.value,
              max: d.weight,
            }))}
          />
          <p className="mt-3 text-[13px] text-ink-muted">
            Sumbu radar memakai persentase capaian tiap dimensi terhadap bobotnya, bukan nilai
            mentah — dimensi berbobot besar dan kecil jadi bisa dibandingkan adil.
          </p>
        </Card>

        <Card>
          <h2 className="mb-3 text-[17px] font-semibold">Nilai per dimensi</h2>
          <BarList
            items={dimensions.map((d) => ({
              label: `${d.code} · ${d.name}`,
              value: d.weight > 0 ? (d.value / d.weight) * 100 : 0,
              hint: `· ${round1(d.value)} dari ${d.weight}${
                d.answeredCount < d.totalCount ? ` · ${d.answeredCount}/${d.totalCount} terisi` : ''
              }`,
            }))}
            formatValue={(v) => `${Math.round(v)}%`}
          />
        </Card>
      </div>

      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-[17px] font-semibold text-ink">Tren Antar Semester</h2>
            <p className="text-[13px] text-ink-secondary">
              Perbandingan capaian setiap dimensi melintasi semester evaluasi (Sumbu X: Dimensi, Sumbu Y: Capaian Skor/Persentase).
            </p>
          </div>
          {trendPoints.length > 1 && (
            <div className="flex rounded-[var(--radius-button)] border border-border bg-app p-0.5 text-xs font-medium">
              <button
                type="button"
                onClick={() => setTrendView('dimensions')}
                className={cn(
                  'rounded-[calc(var(--radius-button)-2px)] px-3 py-1.5 transition-colors cursor-pointer',
                  trendView === 'dimensions'
                    ? 'bg-white shadow-xs text-ink font-semibold'
                    : 'text-ink-secondary hover:text-ink',
                )}
              >
                Per Dimensi (Batang & Garis)
              </button>
              <button
                type="button"
                onClick={() => setTrendView('overall')}
                className={cn(
                  'rounded-[calc(var(--radius-button)-2px)] px-3 py-1.5 transition-colors cursor-pointer',
                  trendView === 'overall'
                    ? 'bg-white shadow-xs text-ink font-semibold'
                    : 'text-ink-secondary hover:text-ink',
                )}
              >
                Indeks Total
              </button>
            </div>
          )}
        </div>

        {trendView === 'dimensions' ? (
          <DimensionTrendChart trend={trend} />
        ) : (
          <TrendChart points={trendPoints} />
        )}
      </Card>
    </div>
  );
}
